import { createContext, useContext, useMemo, useState } from 'react'
import { toast } from 'react-hot-toast'
import TimeAgo from 'javascript-time-ago'
import en from 'javascript-time-ago/locale/en'

import { useAuth } from 'hooks/useAuth'
import { getProfileStatus, settleProfileResult } from 'hooks/profile-result'
import type { ProfileResult } from 'hooks/profile-result'
import {
	FullEvent,
	SearchEventTypeEnum,
	SupportedCurrencyType,
	useGeneralGetMyEventsLazyQuery,
	useGetCurrentUserLazyQuery,
	useGetManagedClubsLazyQuery,
	useGetSupportedCurrenciesQuery,
	useGetTermsConditionsAndPrivacyQuery,
	useGetTransactionFeesLazyQuery,
} from 'graphql/generated/scheme'
import Apollo from 'graphql/apollo'
import { useLocalStorage } from './useLocalStorage'
import { trueErrorMessage } from 'utils/error'
import { ManageClubType } from 'graphql/type'
import useMixpanel from './useMixpanel'

const defaultRequirement = {
	type: 'none',
}

const StoreContext = createContext<any>(null)

TimeAgo.addDefaultLocale(en)

export const StoreProvider = ({ children }: any) => {
	const apolloClient = Apollo.getInstance()
	const { token, authInfo, setAuthInfo, clearAuth } = useAuth()
	const [title, setTitle] = useState<any>(null)
	const [header, setHeader] = useState<any>(null)
	const [hasBackArrow, setHasBackArrow] = useState<any>(true)
	const [backTo, setBackTo] = useState<any>(null)
	const [blur, setBlur] = useState<boolean>(false)
	const [loadingLogout, setLoadingLogout] = useState<boolean>(false)
	const [user, setUser] = useLocalStorage('user', null)
	// How the latest profile request ended, and for which session token. The persisted `user` may belong to an
	// earlier session or account, so only a request made under the current token confirms it.
	const [profileResult, setProfileResult] = useState<ProfileResult | null>(null)
	const profileStatus = getProfileStatus(profileResult, token)
	const [currentTerms, setCurrentTerms] = useState<any>(null)
	const [openDeleteAccount, setOpenDeleteAccount] = useState<boolean>(false)
	const [didYouKnowContent, setDidYouKnowContent] = useState<any>(null)
	const [transactionFees, setTransactionFees] = useState<any>(null)
	const [payouts, setPayouts] = useState<any>(null)
	const [myEvents, setMyEvents] = useState<any[]>([])
	const [myClubs, setMyClubs] = useState<ManageClubType[]>([])
	// True once the managed-clubs request has completed at least once. Distinguishes
	// "member manages no clubs" from "not fetched yet" (myClubs starts as [] and the
	// lazy query's `loading` is false before it is first invoked).
	const [myClubsLoaded, setMyClubsLoaded] = useState<boolean>(false)
	// True when the last managed-clubs request failed. Consumers must not treat this as
	// "member manages no clubs" — they should offer a retry instead.
	const [myClubsError, setMyClubsError] = useState<boolean>(false)
	const [selectedClub, setSelectedClub] = useLocalStorage('selectedClub', null)
	const [selectedEvent, setSelectedEvent] = useState<Partial<FullEvent>>()
	const [supportedCurrencies, setSupportedCurrencies] = useState<SupportedCurrencyType[]>([])
	const { identify: mixpanelIdentity, reset: mixpanelReset } = useMixpanel()

	const [getCurrentUser, { loading: loadingUser }] = useGetCurrentUserLazyQuery({
		notifyOnNetworkStatusChange: true,
		fetchPolicy: 'no-cache',
		onCompleted: data => {
			const me = data?.me || null

			if (me) {
				setUser(me)
				setAuthInfo({
					...authInfo,
					agreeToTermsAndConditions: me.agreeToTermsAndConditions,
					isComplete: !!me.name && !!me.userName && !!me.dateOfBirth && !!me.phoneNumber && !!me.gender,
				})
				mixpanelIdentity(me.userId, {
					email: me.emailAddress,
					name: me.name,
					userName: me.userName,
				})
			}
			// A response without a profile is not a confirmation, and it must not inherit an earlier one.
			setProfileResult(previous => (me ? settleProfileResult(previous, token, true) : { token, loaded: false }))
		},
		onError: error => {
			setProfileResult(previous => settleProfileResult(previous, token, false))
			toast.error(trueErrorMessage(error))
		},
	})
	const [getMyEvents, { loading: loadingMyEvents }] = useGeneralGetMyEventsLazyQuery({
		variables: {
			first: 5,
			criteria: {
				eventType: SearchEventTypeEnum.All,
			},
		},
		notifyOnNetworkStatusChange: true,
		fetchPolicy: 'no-cache',
		onCompleted: data => {
			setMyEvents(data?.event?.myEvents?.nodes ?? [])
		},
		onError: error => {
			toast.error(trueErrorMessage(error))
		},
	})

	const [getMyClubs, { loading: loadingMyClubs }] = useGetManagedClubsLazyQuery({
		variables: {},
		notifyOnNetworkStatusChange: true,
		fetchPolicy: 'no-cache',
		onCompleted: data => {
			setMyClubs(data?.me?.managedClubs ?? [])
			setSelectedClub(data?.me?.managedClubs?.[0])
			setMyClubsError(false)
			setMyClubsLoaded(true)
		},
		onError: error => {
			setMyClubsError(true)
			toast.error(trueErrorMessage(error))
		},
	})

	const { loading: loadingSupportedCurrencies } = useGetSupportedCurrenciesQuery({
		fetchPolicy: 'no-cache',
		onCompleted: data => {
			setSupportedCurrencies(data?.supportedCurrencies ?? [])
		},
	})

	const { loading: loadingTermsConditionsAndPrivacy } = useGetTermsConditionsAndPrivacyQuery({
		onCompleted: data => {
			setCurrentTerms(data?.termsAndConditions?.current || null)
		},
	})

	const [getTransactionFees, { loading: transactionFeesLoading, refetch: transactionFeesRefetch }] =
		useGetTransactionFeesLazyQuery({
			notifyOnNetworkStatusChange: true,
			fetchPolicy: 'no-cache',
			onCompleted: data => {
				setTransactionFees(data?.paypal.options || null)
			},
			onError: error => {
				toast.error(trueErrorMessage(error))
			},
		})

	const clearStore = async () => {
		setLoadingLogout(true)

		try {
			// Clear Apollo cache
			await apolloClient.hardReset()

			// Clear React state
			setHeader(null)
			setHasBackArrow(true)
			setBlur(false)
			setUser(null)
			setPayouts(null)
			setBackTo(null)
			setSelectedClub(null)
			setMyEvents([])
			setMyClubs([])
			setMyClubsLoaded(false)
			setMyClubsError(false)

			// Clear auth state
			clearAuth()

			// Reset Mixpanel
			mixpanelReset()
		} catch (error) {
			console.error('Error during logout:', error)
		} finally {
			setLoadingLogout(false)
		}
	}

	const value = useMemo(
		() => ({
			header,
			setHeader,
			hasBackArrow,
			setHasBackArrow,
			blur,
			setBlur,
			user,
			setUser,
			refetchUser: getCurrentUser,
			loadingUser,
			profileStatus,
			clearStore,
			loadingLogout,
			setLoadingLogout,
			title,
			setTitle,
			currentTerms,
			openDeleteAccount,
			setOpenDeleteAccount,
			loadingTermsConditionsAndPrivacy,
			didYouKnowContent,
			setDidYouKnowContent,
			transactionFees,
			setTransactionFees,
			getTransactionFees,
			transactionFeesLoading,
			transactionFeesRefetch,
			defaultRequirement,
			payouts,
			setPayouts,
			backTo,
			setBackTo,
			myEvents,
			loadingMyEvents,
			refetchMyEvents: getMyEvents,
			refetchMyClubs: getMyClubs,
			loadingMyClubs,
			myClubs,
			myClubsLoaded,
			myClubsError,
			selectedClub,
			setSelectedClub,
			selectedEvent,
			setSelectedEvent,
			supportedCurrencies,
			loadingSupportedCurrencies,
		}),
		[
			header,
			hasBackArrow,
			blur,
			JSON.stringify(user),
			loadingUser,
			profileStatus,
			loadingLogout,
			title,
			JSON.stringify(currentTerms),
			openDeleteAccount,
			loadingTermsConditionsAndPrivacy,
			didYouKnowContent,
			JSON.stringify(transactionFees),
			JSON.stringify(payouts),
			backTo,
			myEvents,
			loadingMyEvents,
			myClubs,
			loadingMyClubs,
			myClubsLoaded,
			myClubsError,
			selectedClub,
			selectedEvent,
			supportedCurrencies,
			loadingSupportedCurrencies,
		]
	)
	return <StoreContext.Provider value={value}>{children}</StoreContext.Provider>
}

export const useStore = () => {
	const context = useContext(StoreContext)
	if (context === null) {
		throw new Error('useStore must be used within a StoreProvider')
	}
	return context
}
